/* ==========================================================================
   DCA NUTRITION — theme.js
   Vanilla, no dependencies, deferred. Everything here is progressive
   enhancement: every interaction has a no-JS fallback in the markup.
   ========================================================================== */
(function () {
  'use strict';

  var DCA = window.DCA || {};
  var routes = DCA.routes || {};
  var strings = DCA.strings || {};

  /* ---------------------------------------------------------------- utils */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    var fmt = DCA.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }
  DCA.formatMoney = formatMoney;

  var focusTrapStack = [];

  function trapFocus(container, opener) {
    var selectors = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    function handler(e) {
      if (e.key !== 'Tab') return;
      var items = $$(selectors, container).filter(function (el) { return el.offsetParent !== null; });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    focusTrapStack.push({ container: container, handler: handler, opener: opener });
    var firstFocusable = $$(selectors, container).filter(function (el) { return el.offsetParent !== null; })[0];
    if (firstFocusable) { window.setTimeout(function () { firstFocusable.focus(); }, 60); }
  }

  function releaseFocus() {
    var entry = focusTrapStack.pop();
    if (!entry) return;
    entry.container.removeEventListener('keydown', entry.handler);
    if (entry.opener && typeof entry.opener.focus === 'function') entry.opener.focus();
  }

  function lockScroll(on) {
    document.body.classList.toggle('is-locked', !!on);
  }

  /* ------------------------------------------------------------ header h */

  function syncHeaderHeight() {
    var header = $('[data-header]');
    if (!header) return;
    document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
  }
  window.addEventListener('resize', debounce(syncHeaderHeight, 150));
  syncHeaderHeight();

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* Shadow the header once the page scrolls */
  var headerEl = $('[data-header]');
  if (headerEl) {
    var onScrollHeader = function () {
      headerEl.classList.toggle('is-stuck', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScrollHeader, { passive: true });
    onScrollHeader();
  }

  /* ------------------------------------------------------- mobile drawer */

  var mobileNav = $('[data-mobile-nav]');
  if (mobileNav) {
    var openMobile = function (opener) {
      mobileNav.classList.add('is-open');
      mobileNav.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      trapFocus(mobileNav, opener);
    };
    var closeMobile = function () {
      mobileNav.classList.remove('is-open');
      mobileNav.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      releaseFocus();
    };
    $$('[data-mobile-nav-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { openMobile(btn); });
    });
    $$('[data-mobile-nav-close]', mobileNav).forEach(function (btn) {
      btn.addEventListener('click', closeMobile);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) closeMobile();
    });
  }

  /* Close desktop mega menus on outside click / escape */
  document.addEventListener('click', function (e) {
    $$('details[data-mega]').forEach(function (d) {
      if (d.open && !d.contains(e.target)) d.open = false;
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    $$('details[data-mega]').forEach(function (d) { d.open = false; });
  });

  /* ---------------------------------------------------------- cart drawer */

  var drawer = $('[data-cart-drawer]');

  function openDrawer(opener) {
    if (!drawer) { window.location.href = routes.cart; return; }
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    trapFocus(drawer, opener);
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    lockScroll(false);
    releaseFocus();
  }
  DCA.openCart = openDrawer;
  DCA.closeCart = closeDrawer;

  if (drawer) {
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-drawer-close]')) closeDrawer();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });
  }

  $$('[data-cart-open]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      if (!drawer) return; // let the link go to /cart
      e.preventDefault();
      openDrawer(btn);
    });
  });

  /* Refresh the drawer body from the Section Rendering API */
  function refreshCart() {
    return fetch(routes.root + '?sections=cart-drawer-contents', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var target = $('[data-cart-drawer-contents]');
        if (target && data['cart-drawer-contents']) {
          var wrapper = document.createElement('div');
          wrapper.innerHTML = data['cart-drawer-contents'];
          var inner = wrapper.querySelector('[data-cart-drawer-contents]') || wrapper;
          target.innerHTML = inner.innerHTML;
        }
        return fetch(routes.cart + '.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('dca:cart:updated', { detail: { cart: cart } }));
        return cart;
      });
  }
  DCA.refreshCart = refreshCart;

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
    $$('[data-cart-item-count]').forEach(function (el) { el.textContent = '(' + count + ')'; });
  }

  /* --------------------------------------------------------- add to cart */

  function setBtnState(btn, state) {
    if (!btn) return;
    if (state === 'loading') {
      btn.dataset.originalText = btn.dataset.originalText || btn.innerHTML;
      btn.setAttribute('aria-disabled', 'true');
      btn.innerHTML = '<span class="loader" aria-hidden="true"></span><span class="visually-hidden">' + (strings.adding || 'Adding') + '</span>';
    } else if (state === 'done') {
      btn.innerHTML = (strings.added || 'Added');
      window.setTimeout(function () {
        btn.innerHTML = btn.dataset.originalText || (strings.addToCart || 'Add to cart');
        btn.removeAttribute('aria-disabled');
      }, 1400);
    } else {
      btn.innerHTML = btn.dataset.originalText || btn.innerHTML;
      btn.removeAttribute('aria-disabled');
    }
  }

  function addToCart(formData, btn) {
    setBtnState(btn, 'loading');
    return fetch(routes.cartAdd, {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then(function (res) { return res.json().then(function (body) { return { ok: res.ok, body: body }; }); })
      .then(function (result) {
        if (!result.ok) {
          setBtnState(btn, 'reset');
          showFormError(btn, result.body.description || result.body.message || strings.error);
          return null;
        }
        setBtnState(btn, 'done');
        return refreshCart().then(function (cart) {
          openDrawer(btn);
          return cart;
        });
      })
      .catch(function () {
        setBtnState(btn, 'reset');
        showFormError(btn, strings.error);
      });
  }
  DCA.addToCart = addToCart;

  function showFormError(btn, message) {
    if (!btn) return;
    var form = btn.closest('form');
    if (!form) { window.alert(message); return; }
    var box = form.querySelector('[data-form-error]');
    if (!box) {
      box = document.createElement('div');
      box.className = 'form-status form-status--error';
      box.setAttribute('data-form-error', '');
      box.setAttribute('role', 'alert');
      form.appendChild(box);
    }
    box.textContent = message;
    box.hidden = false;
    window.setTimeout(function () { box.hidden = true; }, 6000);
  }

  /* Delegated: any form marked data-quick-add or data-product-form */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches('[data-quick-add], [data-product-form], .pdp__form')) return;
    e.preventDefault();
    var btn = form.querySelector('[data-add-btn]');
    addToCart(new FormData(form), btn);
  });

  /* ------------------------------------------------- cart line mutations */

  function changeLine(line, quantity) {
    return fetch(routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshCart(); });
  }

  document.addEventListener('click', function (e) {
    var remove = e.target.closest('[data-remove]');
    if (remove) {
      e.preventDefault();
      changeLine(parseInt(remove.dataset.line, 10), 0);
      return;
    }

    var up = e.target.closest('[data-qty-up]');
    var down = e.target.closest('[data-qty-down]');
    if (!up && !down) return;

    var wrap = (up || down).closest('.qty');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;

    var current = parseInt(input.value, 10) || 0;
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 1;
    var next = up ? current + 1 : current - 1;
    if (next < min) next = min;
    input.value = next;

    if (input.hasAttribute('data-line')) {
      changeLine(parseInt(input.dataset.line, 10), next);
    } else {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-qty-input][data-line]');
    if (!input) return;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    changeLine(parseInt(input.dataset.line, 10), qty);
  });

  /* ----------------------------------------------------------- accordions */
  /* Single-open behaviour for groups marked data-accordion-exclusive */
  $$('[data-accordion-exclusive]').forEach(function (group) {
    var items = $$('details', group);
    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        items.forEach(function (other) { if (other !== item) other.open = false; });
      });
    });
  });

  /* --------------------------------------------------------------- tabs */
  $$('[data-tabs]').forEach(function (group) {
    var tabs = $$('[role="tab"]', group);
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        tabs.forEach(function (t) {
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          var active = t === tab;
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          if (panel) panel.hidden = !active;
        });
      });
    });
  });

  /* ------------------------------------------------------ reveal on scroll */
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    $$('.reveal').forEach(function (el) { io.observe(el); });
  } else {
    $$('.reveal').forEach(function (el) { el.classList.add('is-in'); });
  }

  /* --------------------------------------------------------- PLP sorting */
  var sortSelect = $('[data-sort-by]');
  if (sortSelect) {
    sortSelect.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* Auto-submit facet form on checkbox change */
  var facetForm = $('[data-facet-form]');
  if (facetForm) {
    facetForm.addEventListener('change', function (e) {
      if (e.target.type === 'checkbox' || e.target.tagName === 'SELECT') {
        facetForm.submit();
      }
    });
  }

  /* Mobile facet drawer */
  var facetPanel = $('[data-facets]');
  $$('[data-facet-toggle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (!facetPanel) return;
      var open = facetPanel.hasAttribute('data-open');
      if (open) { facetPanel.removeAttribute('data-open'); facetPanel.hidden = true; }
      else { facetPanel.setAttribute('data-open', ''); facetPanel.hidden = false; }
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  });

  function syncFacetVisibility() {
    if (!facetPanel) return;
    if (window.matchMedia('(min-width: 990px)').matches) {
      facetPanel.hidden = false;
    } else if (!facetPanel.hasAttribute('data-open')) {
      facetPanel.hidden = true;
    }
  }
  window.addEventListener('resize', debounce(syncFacetVisibility, 150));
  syncFacetVisibility();

  window.DCA = DCA;
})();
